import {
  CONTAINER_STATUSES,
  statusLabel,
  type StatusDef,
} from '@/features/dashboard/data/claudeMockData';
import type { ShippingDirection } from '@/contexts/DirectionContext';

/**
 * Statusuri de container/booking normalizate în UI.
 *
 * Pe import backend-ul trimite: in_transit | transshipment | black_sea |
 * discharged | in_delivery | completed.
 * Pe export apar în plus `booked` (booking creat, marfa încă nu e la bord) și
 * `loaded` (container încărcat pe navă în Constanța).
 *
 * `CONTAINER_STATUSES` (dashboard/data/claudeMockData) e sursa unică de adevăr
 * pentru toate statusurile, inclusiv `booked` și `loaded`. Alias-ul de mai jos
 * există pentru lizibilitate la apel; NU redefini statusuri aici — o a doua
 * definiție ar diverge de dashboard (care folosește harta canonică direct) și
 * același status ar apărea cu badge diferit în pagini diferite.
 */
/** Harta completă (import + export). Folosește-o în locul lui CONTAINER_STATUSES. */
export const ALL_CONTAINER_STATUSES: Record<string, StatusDef> = CONTAINER_STATUSES;

/** Id-urile cunoscute; `(string & {})` păstrează compatibilitatea cu orice string venit din API. */
export type ContainerStatusId =
  | 'booked'
  | 'loaded'
  | 'in_transit'
  | 'transshipment'
  | 'black_sea'
  | 'discharged'
  | 'in_delivery'
  | 'completed'
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {});

/**
 * Eticheta unui status, cu fallback sigur: dacă backend-ul trimite un id
 * necunoscut afișăm `rawStatus`-ul (sau id-ul brut) în loc să dispară badge-ul.
 */
export function containerStatusLabel(
  statusId: string | null | undefined,
  lang: 'ro' | 'en',
  fallback?: string | null,
): string {
  if (!statusId) return fallback || '—';
  const known = ALL_CONTAINER_STATUSES[statusId];
  if (known) return statusLabel(ALL_CONTAINER_STATUSES, statusId, lang);
  return fallback || statusId;
}

/** Clasa de badge pentru un status; statusurile necunoscute primesc badge neutru. */
export function containerStatusBadge(statusId: string | null | undefined): string {
  if (!statusId) return 'badge-neutral';
  return ALL_CONTAINER_STATUSES[statusId]?.badge || 'badge-neutral';
}

/**
 * Ordinea statusurilor în filtre, per direcție. Import: întâi ce ajunge cel mai curând
 * (Marea Neagră → transbordare → nava mamă), apoi etapele de după sosire.
 */
const IMPORT_FILTER_ORDER = [
  'black_sea',
  'transshipment',
  'in_transit',
  'discharged',
  'in_delivery',
  'completed',
];

/**
 * Export: Creat/Introducere VGM → booked, Încărcat → loaded,
 * În tranzit - F/V (feeder, plecare din Constanța) → black_sea,
 * În portul de transshipment (Istanbul, așteaptă nava mamă) → transshipment,
 * În tranzit - M/V (nava mamă spre destinație) → in_transit,
 * Descărcat → discharged, Finalizat → completed.
 */
const EXPORT_FILTER_ORDER = [
  'booked',
  'loaded',
  'black_sea',
  'transshipment',
  'in_transit',
  'discharged',
  'completed',
];

export interface StatusFilterOption {
  id: string;
  labelRo: string;
  labelEn: string;
}

/**
 * Opțiunile de filtrare pe status, inclusiv „Toate", adaptate direcției curente.
 */
export function statusFilterOptions(direction: ShippingDirection): StatusFilterOption[] {
  const order = direction === 'export' ? EXPORT_FILTER_ORDER : IMPORT_FILTER_ORDER;
  return [
    { id: 'all', labelRo: 'Toate', labelEn: 'All' },
    ...order
      .map((id) => ALL_CONTAINER_STATUSES[id])
      .filter((s): s is StatusDef => Boolean(s))
      .map((s) => ({ id: s.id, labelRo: s.labelRo, labelEn: s.labelEn })),
  ];
}
